import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Link as RouterLink, useHistory, Route, Switch, useLocation } from 'react-router-dom';
import { setDealerInfo, setOrderInfo } from 'src/actions/claimActions';
import { useDispatch } from 'react-redux';
import {
    Box,
    Card,
    CardContent,
    Container,
    Typography,
    Grid,
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import externalClaimsTheme from '../externalClaimTheme';
import Page from 'src/components/Page';
import Logo from 'src/components/Logo';
import LanguageSelector from 'src/components/LanguageSelector';
import LoginForm from './LoginForm';
import {
    externalLogin,
} from '../../../actions/accountActions';
import claimService from 'src/services/claimService';



const useStyles = makeStyles((theme) => ({
    root: {
        justifyContent: 'center',
        backgroundColor: theme.palette.background.dark,
        display: 'flex',
        height: '100%',
        minHeight: '100vh',
        flexDirection: 'column',
        backgroundImage: 'url(https://nsdstaticasset.blob.core.windows.net/assets/glass%20claim%20dot%20backdrop.png)', // Set the background image
        backgroundSize: 'cover', // Ensure the image covers the entire area
        backgroundPosition: 'center', // Center the image
        backgroundRepeat: 'no-repeat',
        paddingBottom: 80,
        paddingTop: 80,
    },
    backButton: {
        marginLeft: theme.spacing(2),
    },
    text: {
        color: '#869FAC',
        fontWeight: 200,
        fontSize: 44,
        marginLeft: theme.spacing(3),
    },
    card: {
        height: 570,
        width: 958,
        overflow: 'visible',
        display: 'flex',
        position: 'relative',
        borderRadius: 12,
        backgroundImage: 'url(https://nsdstaticasset.blob.core.windows.net/assets/glass%20claim%20card%20bg.png)', // Set the background image
        backgroundSize: 'cover', // Ensure the image covers the entire area
        backgroundPosition: 'center', // Center the image
        backgroundRepeat: 'no-repeat',
        '& > *': {
            flexGrow: 1,
            flexBasis: '50%',
            width: '50%',
        },
    },
    content: {
        padding: theme.spacing(8, 4, 3, 4),
    },
    icon: {
        backgroundColor: green[500],
        color: theme.palette.common.white,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1),
        position: 'absolute',
        top: -32,
        left: theme.spacing(3),
        height: 64,
        width: 64,
    },
    media: {
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
        padding: theme.spacing(3),
        color: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
    copyright: {
        fontSize: 14,
        color: '#606e75',
        textAlign: 'center',
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2),
        width: '100%',
        // Only stick to bottom if content is short, otherwise flow after content
        position: 'relative',
        left: 0,
        bottom: 0,
        zIndex: 1300,
        background: 'transparent',
    },
    iconLabel: {
        color: '#869FAC'
    }
}));

/**
 * The main component for logging a user into the application. Accepts
 * a type prop (client or employee to determine) if to enable login through
 * a microsoft login service or through Azure AD. Link or links can be used
 * to pass in additional links (with their corresponding views) to the ExternalLoginView.
 * (e.g. Want to add a register or forgot password link)
 */
function ExternalLoginView({
    onSubmitSuccess,
    logo,
    logoAlt,
    width,
    height,
    className,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = useStyles();
    const theme = useTheme();
    // const [showInfoAlert, setShowInfoAlert] = useState(false);
    const { alt, src: clientCompanyLogo } = theme?.logo?.login || {};


    const [landingOrderInfo, setLandingOrderInfo] = useState(null);
    const [landingClaims, setLandingClaims] = useState([]);
    const location = useLocation();

    const handleSubmit = async (dealerCode, trackingNumber) => {
        // Run externalLogin and getVendorClaims concurrently
        const loginResult = await dispatch(externalLogin(dealerCode, trackingNumber));
        const { dealerInfo, orderInfo } = loginResult;
        if (dealerInfo && orderInfo) {
            dispatch(setDealerInfo({
                dealerName: dealerInfo.dealerName,
                dealerCode: dealerInfo.dealerCode,
                dealerAddress: dealerInfo.dealerAddress,
                dealerCity: dealerInfo.dealerCity,
                dealerState: dealerInfo.dealerState,
                dealerZipCode: dealerInfo.dealerZip,
            }));
            dispatch(setOrderInfo({
                vendorPurchaseOrderId: orderInfo.vendorPurchaseOrderId,
                vendorPurchaseOrderDate: orderInfo.vendorPurchaseOrderDate,
                vendorPurchaseOrder: orderInfo.vendorPurchaseOrder,
                trackingNumber: orderInfo.trackingNumber,
                purchaseOrderPartsList: orderInfo.purchaseOrderPartsList,
                orderMasterControlNumber: orderInfo.orderMasterControlNumber,
            }));
            let claimsResp;
            try {
                claimsResp = await claimService.getVendorClaims({ dealerCode, trackingNumber });
            }
            catch (error) {
                console.log('Error fetching claims:', error);
            }

            setLandingClaims(claimsResp?.claims || []);
            const keys = ['dealerInfo', 'orderInfo', 'claimInfo', 'claimContactInfo'];

            keys.forEach(key => {
                if (localStorage.getItem(key) !== null) {
                    localStorage.removeItem(key);
                }
            });
            // Redirect user to browse-claim page after successful search, with flag
            if (claimsResp?.claims?.length > 0) {
                history.push({
                    pathname: '/glass/view-claim',
                    state: {
                        fromGlassSearchView: true,
                        dealerCode: dealerInfo.dealerCode,
                        trackingNumber: trackingNumber,
                    }
                });
            }
            else {
                history.push('/glass/claim');
            }


        }
    };

    const handleFileClaim = () => {
        onSubmitSuccess();
        history.push('/glass/claim');
    };

    const handleViewClaim = (claimId) => {
        onSubmitSuccess();
        history.push(`/glass/view-claim?claimId=${claimId}`);
    };

    return (
        <ThemeProvider theme={externalClaimsTheme(theme)}>
            <Page className={classes.root} title={t('glassOrderSearch')}>
                <Container maxWidth='md'>
                    <Box mb={4} display='flex' alignItems='center' justifyContent='space-between'>
                        <RouterLink to='/'>
                            {clientCompanyLogo && !logo ? (
                                <Logo
                                    altText={alt}
                                    image={clientCompanyLogo}
                                    width={width}
                                    height={height}
                                    className={className}
                                />
                            ) : (
                                <Logo
                                    mainIcon
                                    image={logo}
                                    altText={logoAlt}
                                    width={228}
                                    height={62}
                                    className={className}
                                />
                            )}
                        </RouterLink>
                        <LanguageSelector />
                    </Box>
                    <Card className={classes.card}>
                        <CardContent className={classes.content}>
                            <Typography className={classes.text}>
                                {t('searchForGlassOrderToGetStarted')}
                            </Typography>
                            <Box mt={6} mr={3} ml={3} mb={3}>
                                <LoginForm onSubmit={handleSubmit} />
                            </Box>
                            <Box my={2}>

                            </Box>
                            <Box mt={5} textAlign="center">
                                <Grid container spacing={4} alignItems='center' justifyContent='center' direction='row'>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle1" gutterBottom className={classes.iconLabel}>
                                            {t('locateClaims')}
                                        </Typography>
                                        <img src={'https://nsdstaticasset.blob.core.windows.net/assets/find claims icon.png'} style={{ width: '60px', height: '60px' }} />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle1" gutterBottom className={classes.iconLabel}>
                                            {t('fileClaims')}
                                        </Typography>
                                        <img src={'https://nsdstaticasset.blob.core.windows.net/assets/file%20claims%20icon.png'} style={{ width: '60px', height: '60px' }} />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle1" gutterBottom className={classes.iconLabel}>
                                            {t('checkStatus')}
                                        </Typography>
                                        <img src={'https://nsdstaticasset.blob.core.windows.net/assets/claim status icon.png'} style={{ width: '60px', height: '60px' }} />
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>

                    </Card>
                </Container>
                <Box className={classes.copyright}>
                    <Typography>
                        {t('copyrightNationSafeDrivers')}
                    </Typography>
                </Box>
            </Page>
        </ThemeProvider>
    );
}

ExternalLoginView.propTypes = {
    width: PropTypes.string,
    height: PropTypes.string,
    className: PropTypes.string,
    /**
     * Additional links you wish to render on the login form; make sure you have created
     * the linked routes (if leading to a relative component) first in the appConfig
     */
    links: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string.isRequired,
            to: PropTypes.string.isRequired,
        })
    ),
    /**
     * Callback that runs after a user successfully logs in;
     * TODO: Currently cannot access hook functions here as they are
     * not run/evaluated in the context of react
     */
    onSubmit: PropTypes.func,
    onSubmitSuccess: PropTypes.func,
    /**
     * Sets the logo that appears on the login screen. By default should look into the
     * current theme applied and attempt to pull the logo out of that
     */
    logo: PropTypes.string,
    /**
     * Alt text for the logo image passed in
     */
    logoAlt: PropTypes.string,


};

ExternalLoginView.defaultProps = {
    links: null,
    onSubmitSuccess: () => { },
    logo: null,
    logoAlt: null,
    formJSX: null,
};

export default ExternalLoginView;
